"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  X,
  XCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

type ToastType = "default" | "success" | "error" | "warning" | "info";

type Toast = {
  id: string;
  message: string;
  type: ToastType;
  duration: number;
};

type ToastContextValue = {
  showToast: (message: string, durationMs?: number) => void;
  showSuccess: (message: string, durationMs?: number) => void;
  showError: (message: string, durationMs?: number) => void;
  showWarning: (message: string, durationMs?: number) => void;
  showInfo: (message: string, durationMs?: number) => void;
  dismissToast: (id: string) => void;
};

// ─────────────────────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────────────────────

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

// ─────────────────────────────────────────────────────────────────────────────
// Toast Styles
// ─────────────────────────────────────────────────────────────────────────────

const toastStyles: Record<ToastType, { icon: React.ReactNode; className: string }> = {
  default: {
    icon: null,
    className: "bg-surface border-border",
  },
  success: {
    icon: <CheckCircle2 className="h-4 w-4 text-success" />,
    className: "bg-surface border-success/30",
  },
  error: {
    icon: <XCircle className="h-4 w-4 text-warn" />,
    className: "bg-surface border-warn/30",
  },
  warning: {
    icon: <AlertTriangle className="h-4 w-4 text-warn" />,
    className: "bg-surface border-warn/30",
  },
  info: {
    icon: <Info className="h-4 w-4 text-primary" />,
    className: "bg-surface border-primary/30",
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Toast Component
// ─────────────────────────────────────────────────────────────────────────────

function ToastItem({ 
  toast, 
  onDismiss 
}: { 
  toast: Toast; 
  onDismiss: (id: string) => void;
}) {
  const style = toastStyles[toast.type];
  
  useEffect(() => {
    const timer = setTimeout(() => {
      onDismiss(toast.id);
    }, toast.duration);
    
    return () => clearTimeout(timer);
  }, [toast.id, toast.duration, onDismiss]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -10, scale: 0.95 }}
      transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className={cn(
        "pointer-events-auto flex items-start gap-3 w-full max-w-sm",
        "rounded-[10px] border",
        "px-4 py-3 shadow-raised",
        style.className
      )}
    >
      {style.icon && (
        <div className="flex-shrink-0 mt-0.5">
          {style.icon}
        </div>
      )}
      <p className="flex-1 text-body-sm text-text leading-relaxed">
        {toast.message}
      </p>
      <button
        onClick={() => onDismiss(toast.id)}
        className="flex-shrink-0 p-1 rounded-md text-muted hover:text-ink hover:bg-ink/[0.05] transition-colors"
      >
        <X className="h-4 w-4" />
      </button>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────────────────────

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastIdRef = useRef(0);

  const addToast = useCallback((message: string, type: ToastType, durationMs = 3000) => {
    const id = `toast-${++toastIdRef.current}`;
    setToasts((prev) => [...prev.slice(-4), { id, message, type, duration: durationMs }]);
    return id;
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    (message: string, durationMs?: number) => addToast(message, "default", durationMs),
    [addToast]
  );

  const showSuccess = useCallback(
    (message: string, durationMs?: number) => addToast(message, "success", durationMs),
    [addToast]
  );

  const showError = useCallback(
    (message: string, durationMs?: number) => addToast(message, "error", durationMs ?? 5000),
    [addToast]
  );

  const showWarning = useCallback(
    (message: string, durationMs?: number) => addToast(message, "warning", durationMs ?? 4000),
    [addToast]
  );

  const showInfo = useCallback(
    (message: string, durationMs?: number) => addToast(message, "info", durationMs),
    [addToast]
  );

  const value = useMemo(
    () => ({
      showToast,
      showSuccess,
      showError,
      showWarning,
      showInfo,
      dismissToast,
    }),
    [showToast, showSuccess, showError, showWarning, showInfo, dismissToast]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      
      {/* Toast Container */}
      <div
        aria-live="polite"
        aria-label="Notifications"
        className="pointer-events-none fixed bottom-0 right-0 z-50 flex flex-col gap-2 p-6 max-h-screen overflow-hidden"
      >
        <AnimatePresence mode="popLayout">
          {toasts.map((toast) => (
            <ToastItem
              key={toast.id}
              toast={toast}
              onDismiss={dismissToast}
            />
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function useToastContext(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToastContext must be used within ToastProvider");
  }
  return ctx;
}
